import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ClockCountdown } from "@phosphor-icons/react";
import {
  CheckoutHeldDetails,
  CheckoutHeldReason,
} from "../../../hooks/useCompleteCartPayment";

const TITLES: Record<CheckoutHeldReason, string> = {
  CHECKOUT_PAYMENT_PENDING: "Checking your payment",
  CHECKOUT_PAID_NOT_FULFILLED: "Payment received",
  PAYMENT_IN_PROGRESS: "A payment is already in progress",
  OUTCOME_UNKNOWN: "We couldn't confirm your payment",
};

const FALLBACK_MESSAGE =
  "We lost contact before we could confirm whether this payment went through. Check your orders and wallet balance before trying again.";

/**
 * Shown when the customer must not pay again: the money was taken, may have
 * been taken, or another payment is still running. Same shell as the other
 * checkout overlays, minus any retry affordance.
 */
const PaymentHeldOverlay = ({
  details,
  message,
  onViewOrders,
}: {
  details: CheckoutHeldDetails;
  message?: string;
  onViewOrders: () => void;
}) => (
  <motion.div
    initial={{ y: "100%" }}
    animate={{ y: 0 }}
    exit={{ y: "100%" }}
    transition={{
      type: "tween",
      ease: "anticipate",
    }}
    role="alert"
    aria-live="assertive"
    className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full overflow-y-auto"
  >
    <div className="max-w-2xl flex flex-col items-center justify-center gap-8 px-5">
      <div className="bg-amber-500 rounded-full p-8 text-white">
        <ClockCountdown size={36} />
      </div>
      <div className="flex flex-col items-center gap-2 w-full">
        <div className="text-3xl font-medium">{TITLES[details.reason]}</div>
        {/* Never clamped: this is the whole explanation of what happened to the
            customer's money, and a cut-off sentence is what makes someone pay
            twice. */}
        <div className="w-full">
          {details.reason === "OUTCOME_UNKNOWN"
            ? FALLBACK_MESSAGE
            : message || FALLBACK_MESSAGE}
        </div>
        <div className="font-medium">Please do not pay again.</div>
        {details.reference && (
          <div className="text-gray-500 text-sm">
            Reference: <span className="font-medium">{details.reference}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-4">
        <button
          onClick={onViewOrders}
          type="button"
          className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
        >
          <span>View Orders</span>
        </button>
        <Link to="/support" className="text-visaro-orange font-medium underline">
          Contact support
        </Link>
      </div>
    </div>
  </motion.div>
);

export default PaymentHeldOverlay;
